import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Typography, Space, Button, Avatar, Tag, Spin, Alert } from 'antd';
import {
    UserOutlined,
    MailOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ArrowLeftOutlined,
    EditOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../components/ThemeProvider';

const { Title, Text } = Typography;

const ProfilePage: React.FC = () => {
    const { user, loading, token } = useAuth();
    const navigate = useNavigate();
    const { isDark } = useTheme();

    // Redirect to login if not authenticated
    React.useEffect(() => {
        if (!token) {
            navigate('/login');
        }
    }, [token, navigate]);

    if (!token) {
        return null;
    }

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '50vh',
                backgroundColor: isDark ? '#141414' : '#fff',
                color: isDark ? '#fff' : '#000'
            }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!user) {
        return (
            <div style={{
                padding: '24px',
                maxWidth: '600px',
                margin: '0 auto',
                backgroundColor: isDark ? '#141414' : '#fff',
                color: isDark ? '#fff' : '#000'
            }}>
                <Alert
                    message="Profile Not Available"
                    description="Unable to load your profile. Please try logging in again."
                    type="error"
                    showIcon
                    action={
                        <Button size="small" onClick={() => navigate('/login')}>
                            Go to Login
                        </Button>
                    }
                />
            </div>
        );
    }

    return (
        <div style={{
            padding: '24px',
            maxWidth: '600px',
            margin: '0 auto',
            backgroundColor: isDark ? '#141414' : '#fff',
            color: isDark ? '#fff' : '#000',
            minHeight: '100vh'
        }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Header */}
                <Space align="center">
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(-1)}
                        type="text"
                        style={{ color: isDark ? '#fff' : '#000' }}
                    >
                        Back
                    </Button>
                    <Title level={2} style={{
                        margin: 0,
                        color: isDark ? '#fff' : '#000'
                    }}>
                        Profile
                    </Title>
                </Space>

                {/* Profile Card */}
                <Card
                    style={{
                        backgroundColor: isDark ? '#1f1f1f' : '#fff',
                        borderColor: isDark ? '#434343' : '#d9d9d9'
                    }}
                >
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {/* Avatar and Basic Info */}
                        <Space align="center" size="large">
                            <Avatar
                                size={80}
                                icon={<UserOutlined />}
                                style={{
                                    backgroundColor: isDark ? '#1890ff' : '#1890ff',
                                    fontSize: '32px'
                                }}
                            />
                            <Space direction="vertical" size="small">
                                <Title level={3} style={{
                                    margin: 0,
                                    color: isDark ? '#fff' : '#000'
                                }}>
                                    {user.fullname}
                                </Title>
                                <Text type="secondary" style={{
                                    fontSize: '16px',
                                    color: isDark ? '#a6a6a6' : '#666'
                                }}>
                                    @{user.username}
                                </Text>
                            </Space>
                        </Space>

                        {/* Profile Details */}
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Card
                                size="small"
                                style={{
                                    backgroundColor: isDark ? '#262626' : '#f8f9fa',
                                    borderColor: isDark ? '#434343' : '#d9d9d9'
                                }}
                            >
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <Space align="center">
                                        <MailOutlined style={{ color: isDark ? '#a6a6a6' : '#666' }} />
                                        <Text strong style={{ color: isDark ? '#fff' : '#000' }}>
                                            Email Address
                                        </Text>
                                    </Space>
                                    <Text style={{ color: isDark ? '#fff' : '#000' }}>
                                        {user.email}
                                    </Text>
                                    <Space align="center">
                                        {user.emailVerified ? (
                                            <Tag color="green" icon={<CheckCircleOutlined />}>
                                                Verified
                                            </Tag>
                                        ) : (
                                            <Tag color="red" icon={<CloseCircleOutlined />}>
                                                Not Verified
                                            </Tag>
                                        )}
                                    </Space>
                                </Space>
                            </Card>

                            {/* Account Information */}
                            <Card
                                size="small"
                                style={{
                                    backgroundColor: isDark ? '#262626' : '#f8f9fa',
                                    borderColor: isDark ? '#434343' : '#d9d9d9'
                                }}
                            >
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <Text strong style={{ color: isDark ? '#fff' : '#000' }}>
                                        Account Information
                                    </Text>
                                    <Space direction="vertical" size="small">
                                        <Text type="secondary" style={{ color: isDark ? '#a6a6a6' : '#666' }}>
                                            <strong style={{ color: isDark ? '#fff' : '#000' }}>Username:</strong> {user.username}
                                        </Text>
                                        <Text type="secondary" style={{ color: isDark ? '#a6a6a6' : '#666' }}>
                                            <strong style={{ color: isDark ? '#fff' : '#000' }}>Full Name:</strong> {user.fullname}
                                        </Text>
                                        <Text type="secondary" style={{ color: isDark ? '#a6a6a6' : '#666' }}>
                                            <strong style={{ color: isDark ? '#fff' : '#000' }}>Email Status:</strong> {user.emailVerified ? 'Verified' : 'Not Verified'}
                                        </Text>
                                    </Space>
                                </Space>
                            </Card>

                            {/* Actions */}
                            <Space>
                                <Button
                                    type="primary"
                                    icon={<EditOutlined />}
                                    style={{
                                        backgroundColor: isDark ? '#1890ff' : '#1890ff',
                                        borderColor: isDark ? '#1890ff' : '#1890ff'
                                    }}
                                    onClick={() => {
                                        // TODO: Implement edit profile functionality
                                        alert('Edit profile functionality coming soon!');
                                    }}
                                >
                                    Edit Profile
                                </Button>
                                {/* Show Change Password button only for users with local auth provider */}
                                {(!user.authProvider || user.authProvider === 'local') && (
                                    <Button
                                        style={{
                                            color: isDark ? '#fff' : '#000',
                                            borderColor: isDark ? '#434343' : '#d9d9d9',
                                            backgroundColor: isDark ? '#1f1f1f' : '#fff'
                                        }}
                                        onClick={() => navigate('/change-password')}
                                    >
                                        Change Password
                                    </Button>
                                )}
                            </Space>
                        </Space>
                    </Space>
                </Card>
            </Space>
        </div>
    );
};

export default ProfilePage;
